import { type ReactNode, useCallback, useMemo } from "react";
import { Theme } from "tamagui";
import type { ContainerLayoutProps } from "../../utils/layout";
import { View } from "../Stacks";
import { SelectionGroupContext } from "./SelectionContext";
import { SelectionRow } from "./SelectionRow";
import { SelectionTile } from "./SelectionTile";
type SelectionGroupBaseProps = {
  disabled?: boolean;
  showSelector?: boolean;
  label?: string;
  children: ReactNode;
} & ContainerLayoutProps;
type SelectionGroupProps = SelectionGroupBaseProps & ({
  mode: "radio";
  value: string;
  onChange: (value: string) => void;
} | {
  mode: "checkbox" | "switch";
  value: string[];
  onChange: (value: string[]) => void;
});
const SelectionGroupRoot = ({
  mode,
  value,
  onChange,
  disabled = false,
  showSelector = false,
  label,
  children,
  flexDirection = "column",
  gap = "$sp1.5",
  ...layoutProps
}: SelectionGroupProps) => {
  const isSelected = useCallback((itemValue: string): boolean => {
    if (mode === "radio") {
      return value === itemValue;
    }
    return Array.isArray(value) && value.includes(itemValue);
  }, [mode, value]);
  const onSelect = useCallback((itemValue: string) => {
    if (mode === "radio") {
      (onChange as (v: string) => void)(itemValue);
    } else {
      const next = value.includes(itemValue) ? value.filter(v => v !== itemValue) : [...value, itemValue];
      (onChange as (v: string[]) => void)(next);
    }
  }, [mode, value, onChange]);
  const contextValue = useMemo(() => ({
    mode,
    isSelected,
    onSelect,
    disabled,
    showSelector
  }), [mode, isSelected, onSelect, disabled, showSelector]);
  return <SelectionGroupContext.Provider value={contextValue} data-sentry-element="SelectionGroupContext.Provider" data-sentry-component="SelectionGroupRoot" data-sentry-source-file="SelectionGroup.tsx">
      <Theme name="v2" data-sentry-element="Theme" data-sentry-source-file="SelectionGroup.tsx">
        <View flexDirection={flexDirection} gap={gap} {...layoutProps} role={mode === "radio" ? "radiogroup" : "group"} aria-label={label} data-sentry-element="View" data-sentry-source-file="SelectionGroup.tsx">
          {children}
        </View>
      </Theme>
    </SelectionGroupContext.Provider>;
};
type SelectionGroupComposition = {
  Row: typeof SelectionRow;
  Tile: typeof SelectionTile;
};
export const SelectionGroup = Object.assign(SelectionGroupRoot, {
  Row: SelectionRow,
  Tile: SelectionTile
}) as typeof SelectionGroupRoot & SelectionGroupComposition;
export type { SelectionGroupProps };